import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, collection, writeBatch, deleteField } from 'firebase/firestore';
import './Profile.css';
import './LoveLetter.css';

const Profile = () => {
  const [nickname, setNickname] = useState('');
  const [partnerIdInput, setPartnerIdInput] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');


  const fetchUserData = useCallback(async () => {
    if (auth.currentUser) {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setUserData(data);
        setNickname(data.nickname || '');
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleSaveNickname = async () => {
    if (!auth.currentUser) return;
    const userDocRef = doc(db, 'users', auth.currentUser.uid);
    try {
      await setDoc(userDocRef, { nickname: nickname }, { merge: true });
      setFeedback('Nickname updated successfully!');
    } catch (error) {
      console.error("Error updating nickname: ", error);
      setFeedback('Failed to update nickname.');
    }
  };

  const handleLinkPartner = async () => {
    if (!partnerIdInput) {
      setFeedback("Please enter your partner's User ID.");
      return;
    }
    if (partnerIdInput === auth.currentUser.uid) {
      setFeedback("You can't link to yourself!");
      return;
    }
    if (!auth.currentUser) return;

    setFeedback('Linking...');
    
    const partnerDocRef = doc(db, 'users', partnerIdInput);
    const partnerDocSnap = await getDoc(partnerDocRef);

    if (!partnerDocSnap.exists() || !partnerDocSnap.data()?.email) {
      setFeedback('Partner not found or account is incomplete. Please check the User ID.');
      return;
    }

    const partnerData = partnerDocSnap.data();
    const currentUserRef = doc(db, 'users', auth.currentUser.uid);
    const currentUserSnap = await getDoc(currentUserRef);
    const currentUserData = currentUserSnap.data() || {};

    if (currentUserData.gardenId || partnerData.gardenId) {
        setFeedback("One or both users are already in a garden. To link, both must not be in a garden. Please contact support to reset.");
        return;
    }

    const batch = writeBatch(db);
    const newGardenRef = doc(collection(db, 'gardens'));

    batch.set(newGardenRef, {
      users: [
        { uid: auth.currentUser.uid, email: auth.currentUser.email },
        { uid: partnerIdInput, email: partnerData.email }
      ],
      memberUids: [auth.currentUser.uid, partnerIdInput],
      currentPlant: null,
      completedPlants: [],
      moods: {}
    });

    // Update current user with gardenId and partnerId
    batch.set(currentUserRef, { 
        gardenId: newGardenRef.id,
        partnerId: partnerIdInput 
    }, { merge: true });

    // Update partner with gardenId and partnerId
    batch.set(partnerDocRef, { 
        gardenId: newGardenRef.id,
        partnerId: auth.currentUser.uid
    }, { merge: true });

    try {
      await batch.commit();
      setFeedback('Partner linked successfully! You now share a garden.');
      fetchUserData(); // Refresh data
    } catch (error) {
      console.error("Error linking partner: ", error);
      setFeedback(`Failed to link partner: ${error.message}`);
    }
  };

  const handleUnlinkPartner = async () => {
    if (!userData?.gardenId) {
      setFeedback("You are not in a garden.");
      return;
    }
    if (!window.confirm("Are you sure you want to unlink? This will delete the shared garden and cannot be undone.")) {
      return;
    }

    setFeedback('Unlinking...');
    const gardenDocRef = doc(db, 'gardens', userData.gardenId);
    const gardenDocSnap = await getDoc(gardenDocRef);

    if (!gardenDocSnap.exists()) {
      setFeedback("Garden not found. Clearing your garden ID.");
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userDocRef, { gardenId: deleteField() }, { merge: true });
      fetchUserData();
      return;
    }

    const gardenData = gardenDocSnap.data();
    const batch = writeBatch(db);

    // Remove gardenId and partnerId from both users
    gardenData.memberUids.forEach(uid => {
      const userRef = doc(db, 'users', uid);
      batch.update(userRef, { 
          gardenId: deleteField(),
          partnerId: deleteField()
        });
    });

    // Delete the garden document
    batch.delete(gardenDocRef);

    try {
      await batch.commit();
      setFeedback('Successfully unlinked and deleted the shared garden.');
      fetchUserData(); // Refresh user data
    } catch (error) {
      console.error("Error unlinking: ", error);
      setFeedback(`Failed to unlink: ${error.message}`);
    }
  };

  if (loading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="profile-container">
      <h1>Your Profile</h1>
      <p><strong>Email:</strong> {auth.currentUser?.email}</p>
      
      <div className="profile-section">
        <h2>Your User ID</h2>
        <div className="user-id-display">
            <p>Share this with your partner to link accounts:</p>
            <input type="text" value={auth.currentUser?.uid} readOnly />
        </div>
      </div>

      <div className="profile-section">
        <h2>Set Your Nickname</h2>
        <input
          type="text"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="Enter your nickname"
        />
        <button onClick={handleSaveNickname} className="action-button">Save Nickname</button>
      </div>

      <div className="profile-section">
        <h2>Link with Partner</h2>
        {userData?.gardenId ? (
          <div>
            <p>You are linked in a garden!</p>
            <button onClick={handleUnlinkPartner} className="action-button danger">Unlink</button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={partnerIdInput}
              onChange={(e) => setPartnerIdInput(e.target.value)}
              placeholder="Enter your partner's User ID"
            />
            <button onClick={handleLinkPartner} className="action-button">Link Partner</button>
          </>
        )}
      </div>

      {feedback && <p className="feedback-message">{feedback}</p>}

      <button onClick={() => auth.signOut()} className="action-button sign-out-button">Sign Out</button>
    </div>
  );
};

export default Profile;
